import type { OfferActionKind } from "@shared/types/roomOffer";
import type { OfferStatus } from "@shared/roomOffers";

/**
 * The CMS's half of POS-2's validation contract (`POS-5`).
 *
 * `roomOfferProblems()` and the 422 body both answer in CODES — `title_required`,
 * `slots_window_not_forward` — and that is deliberate: the same list is rendered
 * here in the operator's language and returned over the wire, and a server that
 * shipped English prose would be deciding the CMS's wording for it. The cost of
 * that decision is this file: somebody has to turn the codes into sentences, and
 * it is us.
 *
 * Two rules hold it together.
 *
 * 1. **Every code gets a FIELD as well as a sentence.** A dialog that says
 *    "something is wrong" at the top while the offending input sits three
 *    sections down is a dialog an operator closes. The field is what lets the
 *    message land under the control that caused it.
 * 2. **An unmapped code still says something true.** POS-2 may add a rule this
 *    file has not heard of, and the failure mode to avoid is a 422 that renders
 *    as an empty red box. {@link offerProblemMessage} falls through to a
 *    generic sentence that still carries the raw code, so it is greppable in a
 *    support conversation rather than invisible.
 *
 * The CMS chrome is English throughout (see `RootLayout`'s own section labels),
 * so these are English. The Swedish an operator sees is the guest-facing copy
 * they type themselves, through `LocalizedTextField`.
 */

/** Where in the form a problem belongs. Drives which control turns red. */
export type OfferField =
  | "title"
  | "action"
  | "price"
  | "fulfil"
  | "slots"
  | "availability"
  | "items"
  // Only POS-14's override dialog has a control for this one on its own: in
  // the offer editor a picture cannot be refused, because there is no way to
  // state one that is neither a URL nor absent.
  | "image";

export interface OfferProblem {
  code: string;
  field: OfferField;
  message: string;
}

/**
 * Code → field + sentence.
 *
 * Written for a reception manager, not for an engineer: each one says what to
 * DO, because "invalid" tells somebody they are wrong and nothing else.
 */
const PROBLEMS: Record<string, { field: OfferField; message: string }> = {
  title_required: {
    field: "title",
    message:
      "Give the offer a title — an untitled offer shows as a blank card on the TV.",
  },

  /* -- the action ------------------------------------------------------- */
  action_missing: {
    field: "action",
    message: "Choose what pressing this offer does.",
  },
  action_kind_invalid: {
    field: "action",
    message: "Choose one of Promotion, Order or Booking.",
  },
  promotion_takes_no_price: {
    field: "action",
    message:
      "A promotion has no price and no times. Switch it to Order or Booking if it should be bookable.",
  },
  promotion_takes_no_items: {
    field: "items",
    message:
      "A promotion has nothing to pick from — remove the list, or make this an Order.",
  },
  order_fulfil_invalid: {
    field: "fulfil",
    message:
      "Say whether the order is brought to the room or collected at the desk.",
  },

  /* -- money ------------------------------------------------------------ */
  price_amount_invalid: {
    field: "price",
    message:
      "The price must be a number and cannot be negative. Leave it empty for free.",
  },
  price_currency_invalid: {
    field: "price",
    message: "Give the price a currency, e.g. kr.",
  },

  /* -- the slot template ------------------------------------------------ */
  slots_missing: {
    field: "slots",
    message:
      "A booking needs either a weekly timetable or “as soon as possible”.",
  },
  slots_mode_invalid: {
    field: "slots",
    message: "Choose either fixed times or “as soon as possible”.",
  },
  slots_days_invalid: {
    field: "slots",
    message:
      "Pick the weekdays the timetable runs on, or leave them all off for every day.",
  },
  slots_start_time_invalid: {
    field: "slots",
    message: "The first bookable time must be a clock time, like 18:00.",
  },
  slots_end_time_invalid: {
    field: "slots",
    message: "The last bookable time must be a clock time, like 21:00.",
  },
  slots_window_not_forward: {
    field: "slots",
    message:
      "The timetable has to end after it starts. A run past midnight is a second offer, not one that wraps — the guest and the hotel would disagree about which day it is.",
  },
  slots_interval_invalid: {
    field: "slots",
    message:
      "Times are offered every 5 to 720 minutes. 30 gives 18:00, 18:30, 19:00 …",
  },
  slots_capacity_invalid: {
    field: "slots",
    message: "Offer at least one place per time, and at most 999.",
  },
  slots_horizon_invalid: {
    field: "slots",
    message: "A guest can book between 1 and 30 days ahead.",
  },
  slots_notice_invalid: {
    field: "slots",
    message:
      "Notice is between 0 minutes and 7 days — a time inside it is not offered at all.",
  },

  /* -- the active window ------------------------------------------------ */
  availability_start_date_invalid: {
    field: "availability",
    message: "The start date must be a real date.",
  },
  availability_end_date_invalid: {
    field: "availability",
    message: "The end date must be a real date.",
  },
  availability_dates_reversed: {
    field: "availability",
    message: "The offer cannot end before it starts.",
  },
  availability_days_invalid: {
    field: "availability",
    message:
      "Pick the weekdays the offer shows on, or leave them all off for every day.",
  },
  availability_start_time_invalid: {
    field: "availability",
    message: "The time the offer appears must be a clock time, like 07:00.",
  },
  availability_end_time_invalid: {
    field: "availability",
    message: "The time the offer disappears must be a clock time, like 22:00.",
  },

  /* -- the pick list ---------------------------------------------------- */
  items_invalid: {
    field: "items",
    message:
      "The list of things to pick could not be read. Remove the rows and add them again.",
  },
  item_invalid: {
    field: "items",
    message: "One of the rows in the list is empty. Remove it or fill it in.",
  },
  item_label_required: {
    field: "items",
    message:
      "Every row needs a name — an unnamed one shows as an empty tile on the TV.",
  },
  /* -- POS-14, one location's override ---------------------------------- */
  override_invalid: {
    field: "action",
    message:
      "This location's override could not be read. Close the dialog and set it again.",
  },
  override_image_invalid: {
    field: "image",
    message:
      "That picture could not be read. Choose one, or switch the field back to Inherit.",
  },

  item_id_duplicated: {
    field: "items",
    message:
      "Two rows share an id, so an order could not be fulfilled. Remove one and add it again.",
  },
};

/** One code, as a field and a sentence. Never returns null — see the docblock. */
export function offerProblemMessage(code: string): OfferProblem {
  const known = PROBLEMS[code];
  if (known) return { code, ...known };
  // A rule this build has not heard of. Say so honestly and keep the code
  // visible: an unexplained refusal is worse than an ugly one.
  return {
    code,
    field: "action",
    message: `The server refused this offer (${code}).`,
  };
}

/**
 * A whole 422 body, or a thrown error, as messages.
 *
 * Takes the error rather than the body because `unwrap()` attaches `code` and
 * `body` to the Error it throws, and every caller would otherwise re-derive the
 * same two lines. Anything that is not an `invalid_offer` refusal returns null
 * so the caller can fall through to its own handling — the same contract
 * `assetInUseMessage()` already uses.
 */
export function offerProblemsOf(err: unknown): OfferProblem[] | null {
  const e = err as { code?: string; body?: { details?: unknown } } | null;
  if (!e || e.code !== "invalid_offer") return null;
  const details = e.body?.details;
  if (!Array.isArray(details)) return [];
  return details
    .filter((d): d is string => typeof d === "string")
    .map(offerProblemMessage);
}

/** What the operator calls each kind. Model names are never user-facing. */
export const OFFER_KIND_LABEL: Record<OfferActionKind, string> = {
  promotion: "Promotion",
  order: "Order",
  booking: "Booking",
};

/** One line under each kind in the picker, saying what the guest gets. */
export const OFFER_KIND_HINT: Record<OfferActionKind, string> = {
  promotion:
    "A card the guest reads. Nothing to press but Back — no price, no list, no times.",
  order:
    "The guest picks from a list and it is brought to the room or held at the desk.",
  booking:
    "The guest picks an hour from a weekly timetable, or asks for staff right away.",
};

/**
 * The status column's words.
 *
 * "Off" and "Ended" are different facts and the list must not merge them: one
 * is a switch somebody flipped, the other is a date that passed, and the fix is
 * a different click each time.
 */
export const OFFER_STATUS_LABEL: Record<OfferStatus, string> = {
  live: "Live now",
  scheduled: "Scheduled",
  ended: "Ended",
  off: "Off",
};
